import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { GET_EVENT } from "../../gql/queries";
import { UPDATE_EVENT } from "../../gql/mutations";
import Nav from "../../../../components/Nav";
import "./EditEvent.css";

const EditEvent = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();

    const { data: eventData, loading } = useQuery(GET_EVENT, {
        variables: { eventId }
    });

    const [updateEvent] = useMutation(UPDATE_EVENT);

    const [formData, setFormData] = useState({
        title: '',
        date: '',
        time: '',
        hijriDate: '',
        program: '',
        venue: '',
        eventType: '',
        eventAudience: 'everyone' // 'men', 'women', or 'everyone'
    });

    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (eventData?.event) {
            const event = eventData.event;
            let dateValue = '';
            if (event.date !== null && event.date !== undefined && event.date !== '') {
                try {
                        let eventDate;
                        if (typeof event.date === 'number' || (typeof event.date === 'string' && /^\d+$/.test(event.date))) {
                            eventDate = new Date(parseInt(event.date));
                        } else {
                            eventDate = new Date(event.date);
                        }
                        if (eventDate && !isNaN(eventDate.getTime()) && eventDate.getTime() !== 0) {
                            dateValue = eventDate.toISOString().split('T')[0];
                        } else {
                            console.warn('Invalid date value, skipping:', event.date);
                        }
                } catch (error) {
                    console.error('Error parsing date:', error, 'Original value:', event.date);
                    dateValue = '';
                }
            }

            let audience = 'everyone';
            if (event.menOnly) {
                audience = 'men';
            } else if (event.womenOnly) {
                audience = 'women';
            }

            setFormData({
                title: event.title || '',
                date: dateValue,
                time: event.time || '',
                hijriDate: event.hijriDate || '',
                program: event.program || '',
                venue: event.venue || '',
                eventType: event.eventType || '',
                eventAudience: audience
            });
        }
    }, [eventData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Parse time string to get hours, minutes, period
    const parseTime = (timeStr) => {
        if (!timeStr) return { hours: '12', minutes: '00', period: 'PM' };
        const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
        if (match) {
            return {
                hours: match[1],
                minutes: match[2],
                period: match[3].toUpperCase()
            };
        }
        return { hours: '12', minutes: '00', period: 'PM' };
    };

    const { hours, minutes, period } = parseTime(formData.time);

    const updateTime = (field, value) => {
        const current = parseTime(formData.time);

        if (field === 'hours') {
            const numValue = parseInt(value) || 12;
            current.hours = Math.min(12, Math.max(1, numValue)).toString();
        } else if (field === 'minutes') {
            const numValue = parseInt(value) || 0;
            current.minutes = Math.min(59, Math.max(0, numValue)).toString().padStart(2, '0');
        } else if (field === 'period') {
            current.period = value;
        }

        const timeString = `${current.hours}:${current.minutes} ${current.period}`;
        handleInputChange('time', timeString);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await updateEvent({
                variables: {
                    eventId: eventId,
                    title: formData.title,
                    date: formData.date,
                    hijriDate: formData.hijriDate,
                    time: formData.time,
                    program: formData.program,
                    venue: formData.venue,
                    eventType: formData.eventType,
                    // Keep existing hosts (required field)
                    hosts: eventData.event.hosts.map(host => host._id),
                    // Convert eventAudience to menOnly and womenOnly flags
                    menOnly: formData.eventAudience === 'men',
                    womenOnly: formData.eventAudience === 'women'
                }
            });

            setShowSuccess(true);
            setTimeout(() => {
                navigate('/events');
            }, 2000);
        } catch (err) {
            console.error('Error updating event:', err);
            alert('Failed to update event. Please try again.');
        }
    };

    if (loading) {
        return (
            <>
                <div className="navAndHeader">
                    <Nav />
                </div>
                <div className="mainContainer">
                    <p>Loading event...</p>
                </div>
            </>
        );
    }

    if (!eventData?.event) {
        return (
            <>
                <div className="navAndHeader">
                    <Nav />
                </div>
                <div className="mainContainer">
                    <p>Event not found</p>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="navAndHeader">
                <Nav />
            </div>
            <div className="mainContainer">
                <div className="edit-event-container">
                    <h1>Edit Event</h1>

                    {showSuccess && (
                        <div className="success-message">
                            Event updated successfully! Redirecting...
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="edit-event-form">
                        <div className="form-section">
                            <label className="form-label">Event Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                className="form-input"
                                required
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label">Event Type</label>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="public"
                                        checked={formData.eventType === 'public'}
                                        onChange={(e) => handleInputChange('eventType', e.target.value)}
                                        required
                                    />
                                    <span>Everyone in jamaat</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="private"
                                        checked={formData.eventType === 'private'}
                                        onChange={(e) => handleInputChange('eventType', e.target.value)}
                                        required
                                    />
                                    <span>By invitation</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-section">
                            <label className="form-label">Event Audience</label>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="everyone"
                                        checked={formData.eventAudience === 'everyone'}
                                        onChange={(e) => handleInputChange('eventAudience', e.target.value)}
                                        required
                                    />
                                    <span>Mardo and Bairao</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="men"
                                        checked={formData.eventAudience === 'men'}
                                        onChange={(e) => handleInputChange('eventAudience', e.target.value)}
                                        required
                                    />
                                    <span>Mardo Only</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        value="women"
                                        checked={formData.eventAudience === 'women'}
                                        onChange={(e) => handleInputChange('eventAudience', e.target.value)}
                                        required
                                    />
                                    <span>Bairao Only</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-section">
                            <label className="form-label">Hijri Date</label>
                            <input
                                type="text"
                                value={formData.hijriDate}
                                onChange={(e) => handleInputChange('hijriDate', e.target.value)}
                                placeholder="e.g., 17th Jamadal Ula"
                                className="form-input"
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label">Date</label>
                            <input
                                type="date"
                                value={formData.date}
                                onChange={(e) => handleInputChange('date', e.target.value)}
                                className="form-input"
                                required
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label">Time</label>
                            <div className="time-picker-edit">
                                <div className="time-inputs">
                                    <input
                                        type="number"
                                        min="1"
                                        max="12"
                                        value={hours}
                                        onChange={(e) => updateTime('hours', e.target.value)}
                                        className="time-input-small"
                                        required
                                    />
                                    <span className="time-separator">:</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={minutes}
                                        onChange={(e) => updateTime('minutes', e.target.value)}
                                        className="time-input-small"
                                        required
                                    />
                                    <div className="period-buttons">
                                        <button
                                            type="button"
                                            className={`period-btn-edit ${period === 'AM' ? 'active' : ''}`}
                                            onClick={() => updateTime('period', 'AM')}
                                        >
                                            AM
                                        </button>
                                        <button
                                            type="button"
                                            className={`period-btn-edit ${period === 'PM' ? 'active' : ''}`}
                                            onClick={() => updateTime('period', 'PM')}
                                        >
                                            PM
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <label className="form-label">Program Details</label>
                            <textarea
                                value={formData.program}
                                onChange={(e) => handleInputChange('program', e.target.value)}
                                placeholder="e.g., Presentation, Namaaz, Darees"
                                className="form-textarea"
                                rows="1"
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label">Venue</label>
                            <input
                                type="text"
                                value={formData.venue}
                                onChange={(e) => handleInputChange('venue', e.target.value)}
                                placeholder="e.g., Markaz"
                                className="form-input"
                                required
                            />
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/events')}
                                className="btn-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-save"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default EditEvent;

